import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Typography, Avatar, IconButton, Modal } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import axiosInstance from '../axiosConfig';
import API_URL from '../config';

const MOTTO_WIDTH = 900;
const MOTTO_FONT = "48px Arial";

const SchoolProfileCover = ({ onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false); // Bearbeitungsmodus
  const [coverData, setCoverData] = useState({
    logoUrl: '',
    name: '',
    motto: '',
    address: '',
    phone: '',
    schoolCoverLogo: true,
  });
  const [showPopup, setShowPopup] = useState(false);
  const [imageTimestamp, setImageTimestamp] = useState(Date.now());
  const [isPreviewAvailable, setisPreviewAvailable] = useState(false);
  const [isMottoTooLong, setIsMottoTooLong] = useState(false);
  const [remainingMottoChars, setRemainingMottoChars] = useState(30); // Standardwert als Fallback

  const measureTextWidth = (text, font) => {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    context.font = font;
  
    // Skalierungsfaktor: Die SVG-ViewBox ist größer, daher muss dies berücksichtigt werden.
    return context.measureText(text).width;
  };

  const getAverageCharWidth = (font) => {
    // Beispiel-Sample: alle Groß- und Kleinbuchstaben
    const sample = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
    const sampleWidth = measureTextWidth(sample, font);
    return sampleWidth / sample.length; // Durchschnittsbreite
  };
  
  const getApproxRemainingChars = (text, maxWidth, font) => {
    const usedWidth = measureTextWidth(text, font);
    const pxRemaining = maxWidth - usedWidth;
  
    if (pxRemaining <= 0) {
      return 0; // Keine Zeichen mehr möglich
    }
  
    // Durchschnitt pro Zeichen
    const avgWidth = getAverageCharWidth(font);
  
    // Schätze, wie viele weitere Zeichen noch passen
    return Math.floor(pxRemaining / avgWidth);
  };
  

  useEffect(() => {
    // Laden der Daten aus der Datenbank
    const token = localStorage.getItem('authToken');
    axiosInstance
      .get('/profile/school-profile', {
        headers: { Authorization: `${token}` },
      })
      .then((response) => {
        if (response.data.profile) {
            let fullAddress = '';
            if (response.data.profile.street && response.data.profile.postalCode && response.data.profile.city) {
                fullAddress = `${response.data.profile.street}, ${response.data.profile.postalCode} ${response.data.profile.city}`
            }
          setCoverData({
            logoUrl: response.data.profile.logoUrl,
            name: response.data.profile.name || '',
            motto: response.data.profile.motto || '',
            address: fullAddress,
            phone: response.data.profile.contactPhone,
            schoolId: response.data.profile._id
          });
        }
      })
      .catch((error) => {
        console.error('Fehler beim Abrufen des Deckblattprofils:', error);
      });
  }, []);

  useEffect(() => {
    if (coverData.schoolId) {
      fetch(`${API_URL}/uploads/school-profiles/${coverData.schoolId}/components/png/Deckblatt.png`)
        .then((response) => {
          if (response.ok) {
            setisPreviewAvailable(true);
          } else {
            setisPreviewAvailable(false);
          }
        })
        .catch((error) => {
          console.error('Fehler beim Überprüfen des Deckblattpfads:', error);
          setisPreviewAvailable(false);
        });
    }
  }, [coverData]);  

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleSave = () => {
    const token = localStorage.getItem('authToken');
    axiosInstance
      .post(
        '/profile/school-cover',
        { ...coverData },
        { headers: { Authorization: `${token}` } }
      )
      .then((response) => {
        setCoverData((prevState) => ({
          ...prevState,
          ...response.data,
        }));
        setImageTimestamp(Date.now());
        onUpdate({ type: "saveSuccess", profile: response.data.profile } );
        setIsEditing(false);
      })
      .catch((error) => {
        console.error('Fehler beim Speichern des Deckblatts:', error);
        onUpdate({ type: "saveError" });
      });
  };

  const handleChange = (event, source) => {
    onUpdate({ type: "editStart" });
    if (source === "textField") {
      const { name, value } = event.target;
    
      if (name === "motto") {
        let newText = value;
    
        // So lange newText breiter als 900 px, kürze das letzte Zeichen
        while (measureTextWidth(newText, MOTTO_FONT) > MOTTO_WIDTH && newText.length > 0) {
          newText = newText.slice(0, -1);
        }
    
        // Falls wir kürzen mussten, ist isMottoTooLong = true.
        const textWasTruncated = newText.length < value.length;
        setIsMottoTooLong(textWasTruncated);
    
        // z.B. Pixelberechnung (optional):
        const used = measureTextWidth(newText, MOTTO_FONT);
        const remainingPx = MOTTO_WIDTH - used;
        setRemainingMottoChars(Math.floor(remainingPx)); 

        const approxCharsLeft = getApproxRemainingChars(newText, MOTTO_WIDTH, MOTTO_FONT);
        setRemainingMottoChars(approxCharsLeft);
    
        setCoverData((prevData) => ({
          ...prevData,
          [name]: newText  // ACHTUNG: Weg mit [name]: value
        }));
      } else {
        setCoverData((prevData) => ({
          ...prevData,
          [name]: value,
        }));
      }
    } else if (source === "checkbox") {
      setCoverData((prevData) => ({
        ...prevData,
        schoolCoverLogo: event.target.checked,
      }));
    }
  };  

  return (
    <Box
      sx={{
        position: 'relative',
        padding: '1rem',
      }}
    >
      <Typography variant="h5">Deckblatt</Typography>

      {/* Editier-Button in der rechten oberen Ecke */}
      {!isEditing && (
        <Button
          variant="contained"
          size="small"
          color='primary'
          sx={{
            backgroundColor: 'primary.light',
            color: '#fff',
            position: 'absolute',
            top: '1rem',
            right: '2rem',
            border: 'none',
            '&:hover': {backgroundColor: 'primary.dark', color: '#fff'} // Etwas dunkler beim Hover
          }}
          onClick={handleEditClick}
        >
          Bearbeiten
        </Button>
      )}

      {!isEditing ? (
        <Box>
          <Box sx={{ marginBottom: '1rem' }}>
            <Typography color="black" variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              Vorschau Deckblatt
            </Typography>
            {isPreviewAvailable ? (
              <Box
                sx={{
                  cursor: 'pointer',
                  display: 'inline-block',
                  marginBottom: '1rem',
                  border: '1px solid #ccc',
                  borderRadius: '8px',
                  overflow: 'hidden',
                }}
                onClick={() => setShowPopup(true)} // Popup öffnen
              >
                <img
                  src={`${API_URL}/uploads/school-profiles/${coverData.schoolId}/components/png/Deckblatt.png?timestamp=${imageTimestamp}`}
                  alt="Deckblatt Vorschau"
                  style={{ width: '100%', maxWidth: '300px' }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                  }}
                />
              </Box>
            ) : (
              <Typography variant="body2" color="textSecondary">
                Füllen Sie die Formular-Daten aus, um das individuelle Deckblatt zu generieren.
              </Typography>
            )}
          </Box>
          <Box sx={{ marginBottom: '1rem' }}>
            <Typography variant="subtitle1" color="black" sx={{ fontWeight: 'bold' }}>
              Schullogo auf Seite integrieren?
            </Typography>
            <Typography
              sx={{
                fontStyle: coverData?.name ? 'normal' : 'italic',
                color: coverData?.name ? 'inherit' : 'gray',
              }}
            >
              {coverData?.schoolCoverLogo ? "Ja" : "Nein"}
            </Typography>
          </Box>
          <Box sx={{ marginBottom: '1rem' }}>
            <Typography variant="subtitle1" color="black" sx={{ fontWeight: 'bold' }}>
              Schullogo
            </Typography>
            <Avatar alt="Schullogo" src={coverData?.logoUrl} sx={{ width: 100, height: 100 }} />
          </Box>

          <Box sx={{ marginBottom: '1rem' }}>
            <Typography color="black" variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              Schulname
            </Typography>
            <Typography
              sx={{
                fontStyle: coverData?.name ? 'normal' : 'italic',
                color: coverData?.name ? 'inherit' : 'gray',
              }}
            >
              {coverData?.name || 'Nicht ausgefüllt'}
            </Typography>
          </Box>

          <Box sx={{ marginBottom: '1rem' }}>
            <Typography color="black" variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              Schulmotto
            </Typography>
            <Typography
              sx={{
                fontStyle: coverData?.motto ? 'normal' : 'italic',
                color: coverData?.motto ? 'inherit' : 'gray',
              }}
            >
              {coverData?.motto || 'Nicht ausgefüllt'}
            </Typography>
          </Box>

          <Box sx={{ marginBottom: '1rem' }}>
            <Typography color="black" variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              Schuladresse
            </Typography>
            <Typography
              sx={{
                fontStyle: coverData?.address ? 'normal' : 'italic',
                color: coverData?.address ? 'inherit' : 'gray',
              }}
            >
              {coverData?.address || 'Nicht ausgefüllt'}
            </Typography>
          </Box>

          <Box sx={{ marginBottom: '1rem' }}>
            <Typography color="black" variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              Telefonnummer
            </Typography>
            <Typography
              sx={{
                fontStyle: coverData?.phone ? 'normal' : 'italic',
                color: coverData?.phone ? 'inherit' : 'gray',
              }}
            >
              {coverData?.phone || 'Nicht ausgefüllt'}
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box component="form">
          {coverData?.logoUrl ? (
          <>
          <Box sx={{ marginBottom: '1rem' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              Schullogo
            </Typography>
            <Avatar alt="Schullogo" src={coverData?.logoUrl} sx={{ width: 100, height: 100, marginBottom: '1rem' }} />
          </Box>
          
          <Box style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
            <Typography variant="subtitle1" sx={{ marginRight: '1rem' }}>
              Möchten Sie das Schullogo auf dem Deckblatt integrieren?
            </Typography>
            <input
              type="checkbox"
              checked={coverData.schoolCoverLogo}
              onChange={(e) => handleChange(e, "checkbox")}
            />
          </Box>
          </>
      ) : (
        <Typography variant="body2" color="error" sx={{ marginBottom: "1rem" }}>
          Sie haben kein Schullogo in Ihren Schulprofildaten hinterlegt, daher wird kein Schullogo auf die Seite integriert.
        </Typography>
      )}
          <TextField
            label="Name der Schule"
            name="name"
            value={coverData?.name || ''}
            onChange={(e) => handleChange(e, "textField")}
            fullWidth
            disabled="true"
            helperText="Die Schulbestätigung wurde erfolgreich geprüft. Eine Änderung ist nicht möglich."
            margin="normal"
            placeholder="Geben Sie den Namen der Schule ein"
          />

          <Box>
          <TextField
            label="Schulmotto"
            name="motto"
            value={coverData?.motto || ""}
            onChange={(e) => handleChange(e, "textField")}
            fullWidth
            margin="normal"
            helperText={
              isMottoTooLong
                ? "Maximale Zeichenanzahl überschritten!"
                : `Verbleibende Zeichen: ${remainingMottoChars}`
            }
            FormHelperTextProps={{
              style: { color: isMottoTooLong ? "red" : "inherit" },
            }}
          />
          </Box>

          <Box>
            <TextField
              label="Schuladresse"
              name="address"
              value={coverData?.address || ''}
              onChange={(e) => handleChange(e, "textField")}
              fullWidth
              margin="normal"
            />
          </Box>

          <Box>
            <TextField
              label="Telefonnummer"
              name="phone"
              value={coverData?.phone || ''}
              onChange={(e) => handleChange(e, "textField")}
              fullWidth
              margin="normal"
            />
          </Box>

          <Button size="small" variant="contained" color="primary" onClick={handleSave} sx={{ mt: 2, bgcolor: 'primary.light', '&:hover': { color: '#fff' }  }} disabled={isMottoTooLong}>
            Speichern
          </Button>
        </Box>
      )}
      {showPopup && (
      <Modal
        open={showPopup}
        onClose={() => setShowPopup(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            width: '80%',
            maxWidth: '600px',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: 24,
            padding: '1.5rem',
            position: 'relative',
            textAlign: 'center',
          }}
        >
          <IconButton
            sx={{
              position: 'absolute',
              top: '0.5rem',
              right: '0.5rem',
            }}
            onClick={() => setShowPopup(false)}
          >
            X
          </IconButton>
          <img
            src={`${API_URL}/uploads/school-profiles/${coverData.schoolId}/components/png/Deckblatt.png?timestamp=${imageTimestamp}`}
            alt="Deckblatt"
            style={{
              width: '100%',
              borderRadius: '8px',
            }}
          />
        </Box>
      </Modal>
    )}
    </Box>
  );
};

export default SchoolProfileCover;
